/* eslint-disable max-len */

import { Combobox as ComboboxPrimitive } from '@base-ui/react';
import * as React from 'react';

import { Button, ButtonVariant } from '@ui/components/base/button';
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupField,
  InputGroupInput,
} from '@ui/components/base/input-group';
import { CheckIcon, ChevronDownIcon, XIcon } from '@ui/components/icons';
import { cn } from '@ui/lib/utils';
import { ZIndex } from '@ui/zIndexUtils';

const Combobox = ComboboxPrimitive.Root;

const ComboboxValue = ({ ...props }: ComboboxPrimitive.Value.Props) => {
  return <ComboboxPrimitive.Value data-slot="combobox-value" {...props} />;
};

const ComboboxTrigger = React.forwardRef<
  React.ComponentRef<typeof ComboboxPrimitive.Trigger>,
  ComboboxPrimitive.Trigger.Props
>(({ className, children, ...props }, ref) => {
  return (
    <ComboboxPrimitive.Trigger
      ref={ref}
      data-slot="combobox-trigger"
      className={cn("[&_svg:not([class*='size-'])]:size-4", className)}
      {...props}
    >
      {children}
      <ChevronDownIcon className="pointer-events-none size-5 fill-(--combobox-chevron-color)" />
    </ComboboxPrimitive.Trigger>
  );
});
ComboboxTrigger.displayName = 'ComboboxTrigger';

const ComboboxClear = React.forwardRef<
  React.ComponentRef<typeof ComboboxPrimitive.Clear>,
  ComboboxPrimitive.Clear.Props
>(({ className, ...props }, ref) => {
  return (
    <ComboboxPrimitive.Clear
      ref={ref}
      data-slot="combobox-clear"
      render={<InputGroupButton variant={ButtonVariant.GHOST} size="icon-xs" />}
      className={cn(className)}
      {...props}
    >
      <XIcon className="pointer-events-none" />
    </ComboboxPrimitive.Clear>
  );
});
ComboboxClear.displayName = 'ComboboxClear';

const ComboboxInput = React.forwardRef<
  React.ComponentRef<typeof InputGroup>,
  ComboboxPrimitive.Input.Props & {
    showTrigger?: boolean;
    showClear?: boolean;
    addon?: React.ReactNode;
    startAddon?: React.ReactNode;
    label?: string;
  }
>(
  (
    {
      className,
      children,
      disabled = false,
      showTrigger = true,
      showClear = false,
      addon,
      startAddon,
      label,
      placeholder,
      ...props
    },
    ref
  ) => {
    return (
      <InputGroupField
        ref={ref}
        className={cn(label && 'h-14 [&_input]:pt-5 [&_input]:pb-1 [&_input]:pl-3.5', className)}
        onPointerDown={(e: React.PointerEvent<HTMLDivElement>) => {
          const target = e.target as HTMLElement;
          if (target.closest('button') || target.tagName === 'INPUT') return;
          e.preventDefault();
        }}
        onClick={(e: React.MouseEvent<HTMLDivElement>) => {
          const target = e.target as HTMLElement;
          if (target.closest('button') || target.tagName === 'INPUT') return;
          (e.currentTarget as HTMLElement).querySelector('input')?.focus();
        }}
      >
        {startAddon && (
          <InputGroupAddon align="inline-start" className={cn('pl-3.5', label && 'pt-5 pb-1 items-end')}>
            {startAddon}
          </InputGroupAddon>
        )}
        <ComboboxPrimitive.Input
          render={<InputGroupInput disabled={disabled} />}
          placeholder={label ? ' ' : placeholder}
          {...props}
        />
        <InputGroupAddon align="inline-end" className="relative z-10">
          {addon}
          {showTrigger && (
            <InputGroupButton
              size="icon-xs"
              variant={ButtonVariant.GHOST}
              render={<ComboboxTrigger />}
              data-slot="input-group-button"
              className="group-has-data-[slot=combobox-clear]/input-group:hidden bg-transparent hover:bg-transparent data-pressed:bg-transparent aria-expanded:bg-transparent"
              disabled={disabled}
            />
          )}
          {showClear && <ComboboxClear disabled={disabled} />}
        </InputGroupAddon>
        {label && (
          <span
            className={cn(
              'pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm font-normal text-muted-foreground transition-all duration-150 ease-in-out',
              'group-has-[[data-slot=input-group-control]:focus]/input-group:top-2.5 group-has-[[data-slot=input-group-control]:focus]/input-group:translate-y-0 group-has-[[data-slot=input-group-control]:focus]/input-group:text-[10px] group-has-[[data-slot=input-group-control]:focus]/input-group:text-muted-foreground',
              'group-has-[input:not(:placeholder-shown)]/input-group:top-2.5 group-has-[input:not(:placeholder-shown)]/input-group:translate-y-0 group-has-[input:not(:placeholder-shown)]/input-group:text-[10px] group-has-[input:not(:placeholder-shown)]/input-group:text-muted-foreground'
            )}
          >
            {label}
          </span>
        )}
        {children}
      </InputGroupField>
    );
  }
);
ComboboxInput.displayName = 'ComboboxInput';

const ComboboxContent = React.forwardRef<
  React.ComponentRef<typeof ComboboxPrimitive.Popup>,
  ComboboxPrimitive.Popup.Props &
    Pick<ComboboxPrimitive.Positioner.Props, 'side' | 'align' | 'sideOffset' | 'alignOffset' | 'anchor'>
>(({ className, side = 'bottom', sideOffset = 6, align = 'start', alignOffset = 0, anchor, ...props }, ref) => {
  return (
    <ComboboxPrimitive.Portal>
      <ComboboxPrimitive.Positioner
        side={side}
        sideOffset={sideOffset}
        align={align}
        alignOffset={alignOffset}
        anchor={anchor}
        className="isolate z-50"
        style={{ zIndex: ZIndex.POPUP }}
      >
        <ComboboxPrimitive.Popup
          ref={ref}
          data-slot="combobox-content"
          data-chips={!!anchor}
          className={cn(
            'group/combobox-content relative max-h-(--available-height) w-(--anchor-width) max-w-(--available-width) min-w-[calc(var(--anchor-width)+--spacing(7))] origin-(--transform-origin) overflow-hidden rounded-lg bg-popover text-popover-foreground shadow-md ring-1 ring-foreground/10 duration-100 data-[chips=true]:min-w-(--anchor-width) data-[side=bottom]:slide-in-from-top-2 data-[side=inline-end]:slide-in-from-left-2 data-[side=inline-start]:slide-in-from-right-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 *:data-[slot=input-group]:m-1 *:data-[slot=input-group]:mb-0 *:data-[slot=input-group]:h-8 *:data-[slot=input-group]:border-input/30 *:data-[slot=input-group]:bg-input/30 *:data-[slot=input-group]:shadow-none data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95',
            className
          )}
          {...props}
        />
      </ComboboxPrimitive.Positioner>
    </ComboboxPrimitive.Portal>
  );
});
ComboboxContent.displayName = 'ComboboxContent';

const ComboboxList = React.forwardRef<React.ComponentRef<typeof ComboboxPrimitive.List>, ComboboxPrimitive.List.Props>(
  ({ className, ...props }, ref) => {
    return (
      <ComboboxPrimitive.List
        ref={ref}
        data-slot="combobox-list"
        className={cn(
          'no-scrollbar max-h-[min(calc(--spacing(72)---spacing(9)),calc(var(--available-height)---spacing(9)))] scroll-py-1 overflow-y-auto overscroll-contain p-1 data-empty:p-0',
          className
        )}
        {...props}
      />
    );
  }
);
ComboboxList.displayName = 'ComboboxList';

const ComboboxItem = React.forwardRef<React.ComponentRef<typeof ComboboxPrimitive.Item>, ComboboxPrimitive.Item.Props>(
  ({ className, children, ...props }, ref) => {
    return (
      <ComboboxPrimitive.Item
        ref={ref}
        data-slot="combobox-item"
        className={cn(
          "relative flex w-full cursor-default items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-hidden select-none data-highlighted:bg-muted data-highlighted:text-foreground data-disabled:pointer-events-none data-disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
          className
        )}
        {...props}
      >
        {children}
        <ComboboxPrimitive.ItemIndicator
          render={<span className="pointer-events-none ml-auto flex size-4 items-center justify-center" />}
        >
          <CheckIcon className="pointer-events-none" />
        </ComboboxPrimitive.ItemIndicator>
      </ComboboxPrimitive.Item>
    );
  }
);
ComboboxItem.displayName = 'ComboboxItem';

const ComboboxGroup = React.forwardRef<
  React.ComponentRef<typeof ComboboxPrimitive.Group>,
  ComboboxPrimitive.Group.Props
>(({ className, ...props }, ref) => {
  return <ComboboxPrimitive.Group ref={ref} data-slot="combobox-group" className={cn(className)} {...props} />;
});
ComboboxGroup.displayName = 'ComboboxGroup';

const ComboboxLabel = React.forwardRef<
  React.ComponentRef<typeof ComboboxPrimitive.GroupLabel>,
  ComboboxPrimitive.GroupLabel.Props
>(({ className, ...props }, ref) => {
  return (
    <ComboboxPrimitive.GroupLabel
      ref={ref}
      data-slot="combobox-label"
      className={cn('px-2 py-1.5 text-xs font-medium text-muted-foreground', className)}
      {...props}
    />
  );
});
ComboboxLabel.displayName = 'ComboboxLabel';

const ComboboxCollection = ({ ...props }: ComboboxPrimitive.Collection.Props) => {
  return <ComboboxPrimitive.Collection data-slot="combobox-collection" {...props} />;
};

const ComboboxEmpty = React.forwardRef<
  React.ComponentRef<typeof ComboboxPrimitive.Empty>,
  ComboboxPrimitive.Empty.Props
>(({ className, ...props }, ref) => {
  return (
    <ComboboxPrimitive.Empty
      ref={ref}
      data-slot="combobox-empty"
      className={cn(
        'hidden w-full justify-center py-2 text-center text-sm text-muted-foreground group-data-empty/combobox-content:flex',
        className
      )}
      {...props}
    />
  );
});
ComboboxEmpty.displayName = 'ComboboxEmpty';

const ComboboxSeparator = React.forwardRef<
  React.ComponentRef<typeof ComboboxPrimitive.Separator>,
  ComboboxPrimitive.Separator.Props
>(({ className, ...props }, ref) => {
  return (
    <ComboboxPrimitive.Separator
      ref={ref}
      data-slot="combobox-separator"
      className={cn('-mx-1 my-1 h-px bg-border', className)}
      {...props}
    />
  );
});
ComboboxSeparator.displayName = 'ComboboxSeparator';

const ComboboxChips = React.forwardRef<
  React.ComponentRef<typeof ComboboxPrimitive.Chips>,
  ComboboxPrimitive.Chips.Props
>(({ className, ...props }, ref) => {
  return (
    <ComboboxPrimitive.Chips
      ref={ref}
      data-slot="combobox-chips"
      className={cn(
        'flex min-h-8 flex-wrap items-center gap-1 rounded-lg border border-input bg-transparent bg-clip-padding px-2.5 py-1 text-sm transition-colors focus-within:border-ring focus-within:ring-3 focus-within:ring-ring/50 has-aria-invalid:border-destructive has-aria-invalid:ring-3 has-aria-invalid:ring-destructive/20 has-data-[slot=combobox-chip]:px-1 dark:bg-input/30 dark:has-aria-invalid:border-destructive/50 dark:has-aria-invalid:ring-destructive/40',
        className
      )}
      {...props}
    />
  );
});
ComboboxChips.displayName = 'ComboboxChips';

const ComboboxChip = React.forwardRef<
  React.ComponentRef<typeof ComboboxPrimitive.Chip>,
  ComboboxPrimitive.Chip.Props & {
    showRemove?: boolean;
  }
>(({ className, children, showRemove = true, ...props }, ref) => {
  return (
    <ComboboxPrimitive.Chip
      ref={ref}
      data-slot="combobox-chip"
      className={cn(
        'flex h-[calc(--spacing(5.25))] w-fit items-center justify-center gap-1 rounded-sm bg-muted px-1.5 text-xs font-medium whitespace-nowrap text-foreground has-disabled:pointer-events-none has-disabled:cursor-not-allowed has-disabled:opacity-50 has-data-[slot=combobox-chip-remove]:pr-0',
        className
      )}
      {...props}
    >
      {children}
      {showRemove && (
        <ComboboxPrimitive.ChipRemove
          render={<Button variant={ButtonVariant.GHOST} size="icon-xs" />}
          className="-ml-1 opacity-50 hover:opacity-100"
          data-slot="combobox-chip-remove"
        >
          <XIcon className="pointer-events-none" />
        </ComboboxPrimitive.ChipRemove>
      )}
    </ComboboxPrimitive.Chip>
  );
});
ComboboxChip.displayName = 'ComboboxChip';

const ComboboxChipsInput = React.forwardRef<
  React.ComponentRef<typeof ComboboxPrimitive.Input>,
  ComboboxPrimitive.Input.Props
>(({ className, ...props }, ref) => {
  return (
    <ComboboxPrimitive.Input
      ref={ref}
      data-slot="combobox-chip-input"
      className={cn('min-w-16 flex-1 outline-none', className)}
      {...props}
    />
  );
});
ComboboxChipsInput.displayName = 'ComboboxChipsInput';

const useComboboxAnchor = () => {
  return React.useRef<HTMLDivElement | null>(null);
};

export {
  Combobox,
  ComboboxChip,
  ComboboxChips,
  ComboboxChipsInput,
  ComboboxCollection,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxGroup,
  ComboboxInput,
  ComboboxItem,
  ComboboxLabel,
  ComboboxList,
  ComboboxSeparator,
  ComboboxTrigger,
  ComboboxValue,
  useComboboxAnchor,
};
